import React, { useEffect, useRef } from "react";
import "./takeover.css";
import { ASSETS, CALENDLY_URL, PRODUCTION_URL } from "../config";
import { usePageSeo, absoluteUrl, ORGANISATION_JSON_LD } from "../useSeo";
import { Header, BookACallLink } from "../Header";
import { FloatingCta, Footer } from "../FooterAndFloating";
import { TakeoverStages, TakeoverOutcomes, TakeoverFocusList } from "./TakeoverSections";
import { EnquiryForm } from "../EnquiryForm";

const PAGE = {
  title: "The Takeover | Interactive Team Training | The Business Playwright",
  description:
    "David Brent wants to buy your business. The Takeover is an interactive team training experience combining live theatre with practical, personalised development.",
  path: "/the-takeover",
  ogImage: ASSETS.takeoverPoster,
  ogImageAlt: "The Takeover theatre poster",
  jsonLd: {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "The Takeover",
    url: absoluteUrl("/the-takeover"),
    description:
      "David Brent wants to buy your business. The Takeover is an interactive team training experience combining live theatre with practical, personalised development.",
    primaryImageOfPage: absoluteUrl(ASSETS.takeoverPoster),
    isPartOf: { "@type": "WebSite", name: "The Business Playwright", url: PRODUCTION_URL },
    publisher: ORGANISATION_JSON_LD,
    potentialAction: { "@type": "ReserveAction", target: CALENDLY_URL },
  },
};

const TIM_ALT = "Tim Oliver performing as a David Brent impersonator";

export default function TheTakeoverPage() {
  usePageSeo(PAGE);
  const heroRef = useRef(null);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="bp-site tk-page" data-testid="takeover-page">
      <Header />
      <main id="main">
        <section className="tk-hero" ref={heroRef} aria-labelledby="tk-hero-title" data-testid="tk-hero">
          <div className="bp-wrap tk-hero-grid">
            <div className="tk-hero-copy">
              <span className="tk-rule" aria-hidden="true" />
              <h1 id="tk-hero-title" className="tk-hero-title" data-testid="tk-hero-title">David Brent wants to buy your business.</h1>
              <p className="tk-hero-sub" data-testid="tk-hero-sub">The unforgettable team training experience.</p>
              <div className="tk-body">
                <p>For one afternoon, your workplace becomes the target of a private-equity takeover.</p>
                <p>Tim Oliver - the world’s leading David Brent impersonator - and two colleagues arrive to assess the business, interrogate your team and decide what needs to change.</p>
                <p>What follows is a completely original interactive story, built around a genuine training session focused on the skills your people actually want to improve.</p>
              </div>
              <BookACallLink className="bp-btn tk-btn" label="BOOK THE TAKEOVER" testId="tk-hero-cta" />
            </div>
            <div className="tk-hero-poster">
              <img src={ASSETS.takeoverPoster} alt="The Takeover theatre poster" width="1024" height="1536" fetchPriority="high" decoding="async" data-testid="tk-hero-poster" />
            </div>
          </div>
        </section>

        <section className="bp-scene tk-scene" aria-labelledby="tk-ridiculous-title" data-testid="tk-ridiculous">
          <div className="bp-wrap tk-two-col">
            <div>
              <h2 id="tk-ridiculous-title" className="tk-h2">This sounds ridiculous</h2>
              <p className="tk-sub">That’s kind of the point.</p>
              <div className="tk-body">
                <p>Most people have experienced training that seemed useful at the time and was almost completely forgotten a week later.</p>
                <p>This is a training and team building experience that is impossible to forget.</p>
                <p>You can tell people something in a presentation.</p>
                <p>Or you can put them inside a story in which they experience it for themselves.</p>
                <p>The Takeover combines the practical value of a training session with the attention and unpredictability of live theatre.</p>
                <p>Plus it’s David Brent attempting to take over your business. You know you want to see that.</p>
              </div>
            </div>
            <figure className="tk-photo">
              <img src={ASSETS.timOliverAction} alt={TIM_ALT} width="649" height="493" loading="lazy" decoding="async" data-testid="tk-action-photo" />
            </figure>
          </div>
        </section>

        <section className="bp-scene tk-scene" aria-labelledby="tk-how-title" data-testid="tk-how">
          <div className="bp-wrap">
            <div className="tk-how-head">
              <h2 id="tk-how-title" className="tk-h2">How does it work?</h2>
              <p className="tk-small">(Without spoiling the story, this is the gist…)</p>
            </div>
            <TakeoverStages />
          </div>
        </section>

        <section className="bp-scene tk-scene" aria-labelledby="tk-leaves-title" data-testid="tk-leaves">
          <div className="bp-wrap">
            <h2 id="tk-leaves-title" className="tk-h2">Your team leaves with…</h2>
            <TakeoverOutcomes />
          </div>
        </section>

        <section className="bp-scene tk-scene" aria-labelledby="tk-personalise-title" data-testid="tk-personalise">
          <div className="bp-wrap">
            <h2 id="tk-personalise-title" className="tk-h2">How can we personalise the experience?</h2>
            <div className="tk-body tk-narrow">
              <p>Every Takeover has a specific training focus.</p>
              <p>The standard version is built around storytelling.</p>
              <p>But we can adapt the training element around the priorities of your organisation.</p>
            </div>
            <TakeoverFocusList />
            <div className="tk-body tk-narrow">
              <p>The specialist session can be delivered by us, by an expert brought into the experience, or by someone from your own team.</p>
              <p>The theatre wraps around the training.</p>
              <p>The training itself remains genuinely useful.</p>
            </div>
          </div>
        </section>

        <section className="bp-scene tk-scene" aria-labelledby="tk-who-title" data-testid="tk-who">
          <div className="bp-wrap tk-two-col tk-two-col--photo-first">
            <figure className="tk-photo">
              <img src={ASSETS.timOliverPortrait} alt={TIM_ALT} width="1080" height="720" loading="lazy" decoding="async" data-testid="tk-portrait-photo" />
            </figure>
            <div>
              <h2 id="tk-who-title" className="tk-h2">Who’s behind it?</h2>
              <div className="tk-body">
                <p className="tk-credit">Created by Craig “Doe” Wilmann aka The Business Playwright</p>
                <p className="tk-credit">Starring Tim Oliver, the world’s leading David Brent impersonator.</p>
                <p className="tk-legal" data-testid="tk-legal">This experience is not affiliated with or endorsed by Ricky Gervais, the BBC or the creators of The Office</p>
                <p>The story told on the day is wholly original.</p>
                <p>The Takeover combines professional theatre, interactive storytelling and practical business development in one afternoon.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="bp-scene tk-scene tk-final" aria-labelledby="tk-final-title" data-testid="tk-final">
          <div className="bp-wrap tk-final-grid">
            <div className="tk-final-inner">
              <h2 id="tk-final-title" className="tk-final-title">BRING THE TAKEOVER TO YOUR WORKPLACE</h2>
              <div className="tk-body">
                <p>Tell us about your team, what you want them to develop and when you’d like the prospective new owners to arrive.</p>
                <p>We’ll take it from there.</p>
              </div>
              <BookACallLink className="bp-btn tk-btn" label="BOOK A 30-MINUTE CALL" testId="tk-final-cta" />
            </div>
            <EnquiryForm production="the-takeover" showFocus prefix="tk-enquiry" buttonClass="bp-btn tk-btn" />
          </div>
        </section>
      </main>
      <Footer />
      <FloatingCta heroRef={heroRef} label="BOOK THE TAKEOVER" testId="tk-floating-cta" />
    </div>
  );
}
